import React, { forwardRef } from 'react';
import { motion, HTMLMotionProps } from 'framer-motion';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { trackEvent } from '../../lib/metaPixel';
function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}
type BaseProps = {
  variant?: 'primary' | 'secondary' | 'outline' | 'cta';
  size?: 'sm' | 'md' | 'lg' | 'xl';
  fullWidth?: boolean;
  className?: string;
  children?: React.ReactNode;
};
type ButtonAsButton = BaseProps & HTMLMotionProps<'button'> & {
  href?: undefined;
};
type ButtonAsLink = BaseProps & Omit<HTMLMotionProps<'a'>, 'href'> & {
  href: string;
  target?: string;
  rel?: string;
};
type ButtonProps = ButtonAsButton | ButtonAsLink;
function getButtonStyles(variant: BaseProps['variant'] = 'primary', size: BaseProps['size'] = 'md', fullWidth?: boolean, className?: string) {
  const isCta = variant === 'cta';
  return cn('relative inline-flex items-center justify-center font-bold transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-cream tracking-wide cursor-pointer no-underline', {
    'rounded-xl': !isCta,
    'rounded-full': isCta,
    'bg-terracotta text-white hover:bg-terracotta-hover focus:ring-terracotta shadow-md': variant === 'primary',
    'bg-forest text-white hover:bg-forest-light focus:ring-forest shadow-md': variant === 'secondary',
    'bg-transparent border-2 border-forest text-forest hover:bg-forest/5 focus:ring-forest': variant === 'outline',
    'bg-gradient-to-b from-honey-light via-honey to-honey-dark text-forest-deep border-2 border-honey-light/80 hover:from-honey hover:to-honey-dark focus:ring-honey shadow-[0_10px_25px_-5px_rgba(212,162,76,0.55)] uppercase': isCta,
    'px-4 py-2 text-sm': size === 'sm',
    'px-6 py-3 text-base': size === 'md',
    'px-8 py-4 text-lg': size === 'lg',
    'px-10 py-5 text-xl': size === 'xl',
    'w-full': fullWidth
  }, className);
}
function getCtaMotionProps(isCta: boolean) {
  return {
    whileHover: {
      scale: isCta ? 1.04 : 1.02,
      y: isCta ? -2 : 0
    },
    whileTap: {
      scale: 0.97
    },
    animate: isCta ? {
      boxShadow: ['0 10px 25px -5px rgba(212, 162, 76, 0.5), 0 0 0 0 rgba(212, 162, 76, 0.6)', '0 14px 35px -5px rgba(212, 162, 76, 0.7), 0 0 0 10px rgba(212, 162, 76, 0)', '0 10px 25px -5px rgba(212, 162, 76, 0.5), 0 0 0 0 rgba(212, 162, 76, 0)']
    } : undefined,
    transition: isCta ? {
      boxShadow: {
        duration: 2.4,
        repeat: Infinity,
        ease: 'easeInOut' as const
      }
    } : undefined
  };
}
export const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>((props, ref) => {
  const {
    className,
    variant = 'primary',
    size = 'md',
    fullWidth,
    children,
    ...rest
  } = props as BaseProps & {
    href?: string;
  } & Record<string, any>;
  const isCta = variant === 'cta';
  const styles = getButtonStyles(variant, size, fullWidth, className);
  const motionProps = getCtaMotionProps(isCta);
  if ('href' in rest && rest.href) {
    const {
      href,
      target = '_blank',
      rel = 'noopener noreferrer',
      onClick,
      ...linkRest
    } = rest;
    const isCheckoutLink = typeof href === 'string' && href.includes('stockdigital.shop');
    const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (isCheckoutLink) {
        trackEvent('InitiateCheckout', {
          content_name: typeof children === 'string' ? children : 'Hogar Natural Checkout'
        });
      }
      if (onClick) onClick(e as any);
    };
    return <motion.a ref={ref as React.Ref<HTMLAnchorElement>} href={href} target={target} rel={rel} onClick={handleClick} className={styles} {...motionProps} {...linkRest} data-id="element-434">
        {children}
      </motion.a>;
  }
  return <motion.button ref={ref as React.Ref<HTMLButtonElement>} className={styles} {...motionProps} {...rest} data-id="element-435">
      {children}
    </motion.button>;
});
Button.displayName = 'Button';